/**
 * Pure helpers for the company ad-name template used by the AdForge
 * "Build ad name from tags" composer.
 *
 * A template is a string with `{slug}` placeholders keyed by tag-category slug
 * (e.g. `{gender}/{talent}-{creative_name}`); everything else is a literal kept
 * verbatim. At compose time each placeholder is replaced with the selected tag
 * label(s) for that category. Empty placeholders — and the literal separators
 * that would dangle around them — are dropped, so a separator only ever renders
 * *between* two non-empty values.
 */

export type TemplateToken =
  | { type: "literal"; value: string }
  | { type: "placeholder"; slug: string }

// Matches `{slug}` where slug is lowercase alphanumeric + underscore (the shape
// of tag-category slugs). Anything that doesn't match stays a literal.
const PLACEHOLDER_RE = /\{([a-z0-9_]+)\}/g

/** Tokenize a template into ordered literal / placeholder segments. */
export function parseTemplate(template: string): TemplateToken[] {
  const tokens: TemplateToken[] = []
  let lastIndex = 0
  // Reset lastIndex on the shared global regex before each run.
  PLACEHOLDER_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = PLACEHOLDER_RE.exec(template)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({
        type: "literal",
        value: template.slice(lastIndex, match.index),
      })
    }
    tokens.push({ type: "placeholder", slug: match[1] })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < template.length) {
    tokens.push({ type: "literal", value: template.slice(lastIndex) })
  }
  return tokens
}

/** Slugs referenced by the template, in order of first appearance. */
export function extractSlugs(template: string): string[] {
  const seen = new Set<string>()
  const slugs: string[] = []
  for (const token of parseTemplate(template)) {
    if (token.type === "placeholder" && !seen.has(token.slug)) {
      seen.add(token.slug)
      slugs.push(token.slug)
    }
  }
  return slugs
}

/**
 * Compose a name from the template and a `slug -> value` map. A placeholder
 * with no (or an empty) value is skipped, and any literal that would sit before
 * the first emitted value or after the last is dropped — so the only literals
 * that survive are those *between* two non-empty values.
 */
export function composeFromTemplate(
  template: string,
  valuesBySlug: Record<string, string>,
): string {
  let result = ""
  // Literal text seen since the last emitted value; only flushed once a
  // subsequent non-empty value confirms it sits between two values.
  let pendingSeparator = ""
  let hasEmitted = false

  for (const token of parseTemplate(template)) {
    if (token.type === "literal") {
      pendingSeparator += token.value
      continue
    }
    const value = (valuesBySlug[token.slug] ?? "").trim()
    if (!value) {
      // Empty placeholder: discard the separator that was leading into it.
      pendingSeparator = ""
      continue
    }
    if (hasEmitted) result += pendingSeparator
    result += value
    pendingSeparator = ""
    hasEmitted = true
  }

  return result
}
